import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { Flex, TextButton } from '@zillow/constellation';
import { Center } from '@zillow/zrs-comet';

import { CTA, GA_EVENTS } from 'app/shared/constants/AnalyticsLeasesModuleConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';

import LeaseCard from '../LeaseCard';

import {
    StyledFlexLeaseCard, ConnectedLeaseContainer, StyledDivider,
} from './StyledComponents';

const {
    CREATE_LEASE_CLICK,
    UPLOAD_LEASE_CLICK,
} = GA_EVENTS;

const {
    CREATE_A_LEASE,
    UPLOAD_A_LEASE,
} = CTA;

const ConnectedLeases = ({ leaseCards, ctaButtons }) => {
    const dispatch = useDispatch();

    const isMultipleLeases = leases => (leases && leases.length && leases.length > 1);

    const handleCTAClick = (text) => {
        switch (text) {
            case CREATE_A_LEASE:
                dispatch(analyticsUtils.track.GAEventV2(CREATE_LEASE_CLICK));
                break;

            case UPLOAD_A_LEASE:
                dispatch(analyticsUtils.track.GAEventV2(UPLOAD_LEASE_CLICK));
                break;

            default:
                console.warn('No case for', text);
                break;
        }
    };

    const renderLeaseCards = leases => leases.map((lease, i) => (
        <StyledFlexLeaseCard
            key={`leaseCard-${i}`}
            className={cx('LeaseCard', {
                'LeaseCard-Even': i % 2 === 0,
                'LeaseCard-Multiple': isMultipleLeases(leases),
            })}
            mediaQuery={{
                xs: { flex: '0 50%' },
                md: { flex: isMultipleLeases(leases) ? '0 50%' : '1 50%' },
                lg: { flex: '0 50%' },
            }}
        >
            <LeaseCard lease={lease} isMultipleLeases={isMultipleLeases(leases)} index={i} id={i} />
        </StyledFlexLeaseCard>
    ));
    const renderCtaButtons = (buttons) => {
        const styledButtons = buttons.map(({ text, url }, i) => (
            <TextButton
                data-test="connected-leases-cta-button"
                as="a"
                href={url}
                key={`connected-leases-cta-button-${i}`}
                onClick={() => handleCTAClick(stringHelper.startCaseRemoveSpace(text))}
            >
                {text}
            </TextButton>

        ));

        if (styledButtons.length === 2) {
            const [firstButton, secondButton] = styledButtons;
            return [firstButton, <StyledDivider key="connected-leases-cta-button-divider" />, secondButton];
        }

        return styledButtons;
    };
    return (
        <div className="ConnectedLeases">
            <ConnectedLeaseContainer
                display="flex"
                mediaQuery={{
                    xs: {
                        flexDirection: 'column',
                    },
                    md: {
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                    },
                    lg: {
                        flexDirection: 'column',
                    },
                }}
            >
                {renderLeaseCards(leaseCards)}
            </ConnectedLeaseContainer>
            <Center
                mediaQuery={{
                    lg: {
                        paddingY: 'xs',
                    },
                }}
            >
                <Flex display="flex" justifyContent="center">
                    {renderCtaButtons(ctaButtons)}
                </Flex>
            </Center>
        </div>
    );
};

ConnectedLeases.propTypes = {
    leaseCards: PropTypes.arrayOf(PropTypes.shape({
        leaseId: PropTypes.number,
        leaseStatusText: PropTypes.string.isRequired,
        leaseStatusType: PropTypes.string.isRequired,
        leaseThumbnailUrl: PropTypes.string,
        leaseName: PropTypes.string,
        leaseDescription: PropTypes.shape({
            type: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
        }),
        ctaButtonText: PropTypes.string.isRequired,
        ctaButtonUrl: PropTypes.string.isRequired,
        overflowButtons: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        })),
    })),
    ctaButtons: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    })),
};

ConnectedLeases.defaultProps = {
    leaseCards: [],
    ctaButtons: [],
};

export default ConnectedLeases;
